'use client'

import Text from '@/app/components/common/Text'
import classNames from 'classnames'
import { ChangeEvent, PropsWithChildren, forwardRef } from 'react'

export interface Props extends PropsWithChildren {
	id: string
	className?: string
	placeholder?: string
	upperPlaceholder?: string
	value?: string
	readonly?: boolean
	loading?: boolean
	onChange?: (value: string) => void
	onFocus?: () => void
	onClick?: () => void
}

const Input = forwardRef<HTMLInputElement, Props>(function Input(
	{
		id,
		value = '',
		placeholder = '',
		upperPlaceholder = '',
		className = '',
		readonly = false,
		loading = false,
		onChange,
		onFocus,
		onClick,
		children,
	},
	ref
) {
	function onValueChange(e: ChangeEvent<HTMLInputElement>) {
		const newValue = e.target.value.trim()
		onChange && onChange(newValue)
	}

	const upperPlaceholderId = `${id}-upper-placeholder`
	const labelId = `${id}-label`

	// Only reference upperPlaceholderId if it exists in the DOM
	const ariaLabelledBy = upperPlaceholder
		? `${upperPlaceholderId} ${labelId}`
		: labelId

	// Ensure sr-only label always has meaningful text
	const accessibleLabel = upperPlaceholder || placeholder || id

	return (
		<div className="relative h-full" data-test-id={id}>
			{upperPlaceholder && (
				<Text
					id={upperPlaceholderId}
					className="input-new__placeholder"
					tag="div"
					size="md"
					color="black"
					font="medium"
				>
					{upperPlaceholder}
				</Text>
			)}
			<label htmlFor={id} id={labelId} className="input-new__placeholder-label">
				<span className="sr-only">{accessibleLabel}</span>
				{children && (
					<div className=" input-new__placeholder-icon">{children}</div>
				)}
				{loading && <div className="input-new__loading"></div>}
				<input
					ref={ref}
					id={id}
					className={classNames(
						'input-new p-4 text-black font-medium',
						{ 'ps-11': children },
						className
					)}
					placeholder={placeholder}
					readOnly={readonly}
					value={value}
					onChange={onValueChange}
					onFocus={onFocus}
					onClick={onClick}
					aria-labelledby={ariaLabelledBy}
				/>
			</label>
		</div>
	)
})

export default Input
